import { useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { Plus, X } from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTranslationApp } from "../../../lib/i18next";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import TextField from "../../../components/Form/TextField";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import {
  BUTTON_ADD,
  BUTTON_CANCEL,
  FIELD_REQUIRED,
  TEMPLATES_ADD_FORMAT,
  TEMPLATES_CUSTOM_FORMAT,
  TEMPLATES_CUSTOM_FORMAT_TITLE,
  TEMPLATES_CUSTOM_HEIGHT,
  TEMPLATES_CUSTOM_WIDTH,
  TEMPLATES_REMOVE_FORMAT,
  TEMPLATES_STRIP_FORMAT_TOO_LARGE,
  ERROR_RESIZE_DIMENSION_MAX_EDGE,
} from "../../../i18n/keysTranslations";
import { RESIZE_MAX_EDGE } from "../../../utils/resizeCustomDimensions";
import { BORDER_RADIUS_BUTTON } from "../../../utils/constants";
import {
  getArtboardLabel,
  listArtboardIDs,
  listAvailablePresets,
} from "../utils/artboards";
import {
  isContinuousPresetAllowed,
  stripToSlideArtboards,
} from "../utils/continuousStripArtboards";
import { buildStripSize, isStripWithinLimits } from "../utils/continuousStrip";

const parsePositiveDimension = (value) => {
  if (value === "" || value === null || value === undefined) {
    return { valid: false, reason: "empty" };
  }
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return { valid: false, reason: "invalid" };
  }
  const rounded = Math.round(num);
  if (rounded > RESIZE_MAX_EDGE) {
    return { valid: false, reason: "maxEdge" };
  }
  return { valid: true, value: rounded };
};

const pxEndAdornment = (
  <InputAdornment position="end" sx={{ ml: 0, mt: 0.25 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontSize: 14,
        fontWeight: 500,
      }}
    >
      px
    </Typography>
  </InputAdornment>
);

// eslint-disable-next-line
export const artboardChipSx = ({ active, disabled, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  height: 32,
  px: 1.25,
  borderRadius: BORDER_RADIUS_BUTTON,
  border: "1px solid",
  borderColor: active ? "transparent" : "#E4E4E7",
  background: active ? theme.palette.button.primary.main : "#fff",
  color: active ? theme.palette.button.primary.contrastText : "text.primary",
  fontSize: "13px",
  fontWeight: 500,
  fontFamily: "inherit",
  appearance: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  userSelect: "none",
  ...(active && !disabled
    ? {
        "&:hover": {
          background: theme.palette.button.primary.hover.main,
          color: theme.palette.button.primary.hover.contrastText,
        },
      }
    : {}),
});

const ArtboardSwitcher = ({
  artboards = {},
  activeArtboardID,
  disabled,
  onSelect,
  onBeforeAddFormat,
  onAddPreset,
  onAddCustom,
  onRemoveActive,
  continuousSlideCount = 0,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [isAddFormatPending, setIsAddFormatPending] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      width: "",
      height: "",
    },
    mode: "onChange",
  });
  const widthValue = watch("width");
  const heightValue = watch("height");
  const parsedWidth = parsePositiveDimension(widthValue);
  const parsedHeight = parsePositiveDimension(heightValue);
  const isContinuous = continuousSlideCount > 0;
  const isCustomStripAllowed =
    !isContinuous ||
    (parsedWidth.valid &&
      parsedHeight.valid &&
      isStripWithinLimits(
        buildStripSize({
          slideWidth: parsedWidth.value,
          slideHeight: parsedHeight.value,
          slideCount: continuousSlideCount,
        })
      ));
  const isCustomSizeReady =
    parsedWidth.valid && parsedHeight.valid && isCustomStripAllowed;
  const artboardIDs = listArtboardIDs({ artboards });
  const slideViewArtboards = isContinuous
    ? stripToSlideArtboards({ artboards, slideCount: continuousSlideCount })
    : artboards;
  const availablePresets = listAvailablePresets({
    artboards: slideViewArtboards,
  });
  const canRemove = artboardIDs.length > 1;

  const requiredRules = {
    required: {
      value: true,
      message: t(FIELD_REQUIRED),
    },
    validate: (value) => {
      const parsed = parsePositiveDimension(value);
      if (parsed.valid || parsed.reason !== "maxEdge") return true;
      return t(ERROR_RESIZE_DIMENSION_MAX_EDGE, { max: RESIZE_MAX_EDGE });
    },
  };

  const closeCustom = () => {
    setIsCustomOpen(false);
    reset({ width: "", height: "" });
  };

  const onClickAddFormat = async (event) => {
    if (disabled || isAddFormatPending) return;
    const anchor = event.currentTarget;
    if (!onBeforeAddFormat) {
      setMenuAnchor(anchor);
      return;
    }
    setIsAddFormatPending(true);
    try {
      const allowed = await onBeforeAddFormat();
      if (allowed) setMenuAnchor(anchor);
    } finally {
      setIsAddFormatPending(false);
    }
  };

  const onConfirmCustom = ({ width, height }) => {
    const submittedWidth = parsePositiveDimension(width);
    const submittedHeight = parsePositiveDimension(height);
    if (!submittedWidth.valid || !submittedHeight.valid) return;
    if (!isCustomStripAllowed) return;
    closeCustom();
    onAddCustom({
      width: submittedWidth.value,
      height: submittedHeight.value,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
      }}
    >
      {artboardIDs.map((id) => {
        const active = id === activeArtboardID;
        return (
          <Box
            key={id}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onSelect(id)}
            sx={{
              ...artboardChipSx({ active, disabled, theme }),
            }}
          >
            {getArtboardLabel({
              artboard: slideViewArtboards[id],
              artboardID: id,
            })}
            {active && canRemove ? (
              <Box
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (disabled) return;
                  onRemoveActive();
                }}
                sx={{
                  display: "inline-flex",
                  ml: 0.25,
                  opacity: 0.8,
                  "&:hover": { opacity: 1 },
                }}
                title={t(TEMPLATES_REMOVE_FORMAT)}
              >
                <X size={12} />
              </Box>
            ) : null}
          </Box>
        );
      })}
      <>
        <Box
          component="button"
          type="button"
          disabled={disabled || isAddFormatPending}
          onClick={onClickAddFormat}
          sx={artboardChipSx({
            active: false,
            disabled: disabled || isAddFormatPending,
            theme,
          })}
          title={t(TEMPLATES_ADD_FORMAT)}
        >
          <Plus size={14} />
          {t(TEMPLATES_ADD_FORMAT)}
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {availablePresets.map((preset) => (
            <MenuItem
              key={preset.id}
              disabled={
                isContinuous &&
                !isContinuousPresetAllowed({
                  preset,
                  slideCount: continuousSlideCount,
                })
              }
              onClick={() => {
                setMenuAnchor(null);
                onAddPreset(preset.id);
              }}
            >
              {preset.label}
              {isContinuous &&
              !isContinuousPresetAllowed({
                preset,
                slideCount: continuousSlideCount,
              }) ? (
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {t(TEMPLATES_STRIP_FORMAT_TOO_LARGE)}
                </Typography>
              ) : null}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setIsCustomOpen(true);
            }}
          >
            {t(TEMPLATES_CUSTOM_FORMAT)}
          </MenuItem>
        </Menu>
        <ModalBasicLayout
          modalOpen={isCustomOpen}
          onCloseModal={closeCustom}
          title={t(TEMPLATES_CUSTOM_FORMAT_TITLE)}
          ariaLabel={t(TEMPLATES_CUSTOM_FORMAT_TITLE)}
          maxWidth="xs"
          fullWidth
          disableScrollLock
        >
          <Box
            component="form"
            onSubmit={handleSubmit(onConfirmCustom)}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              name="width"
              control={control}
              errors={errors}
              label={t(TEMPLATES_CUSTOM_WIDTH)}
              variant="filled"
              type="number"
              fullWidth
              autoFocus
              inputProps={{ min: 1, max: RESIZE_MAX_EDGE }}
              InputProps={{ endAdornment: pxEndAdornment }}
              rules={requiredRules}
            />
            <TextField
              name="height"
              control={control}
              errors={errors}
              label={t(TEMPLATES_CUSTOM_HEIGHT)}
              variant="filled"
              type="number"
              fullWidth
              inputProps={{ min: 1, max: RESIZE_MAX_EDGE }}
              InputProps={{ endAdornment: pxEndAdornment }}
              rules={requiredRules}
            />
            {parsedWidth.valid &&
            parsedHeight.valid &&
            !isCustomStripAllowed ? (
              <Typography variant="caption" color="error">
                {t(TEMPLATES_STRIP_FORMAT_TOO_LARGE)}
              </Typography>
            ) : null}
          </Box>
          <DialogActions
            sx={{
              px: { xs: 3, sm: 5 },
              pb: 3,
              mt: 0.5,
            }}
          >
            <ContainerModalActions
              hasPadding={false}
              propsSecondaryButton={{
                showButton: true,
                text: BUTTON_CANCEL,
                onClick: closeCustom,
              }}
              propsPrimaryButton={{
                showButton: true,
                text: BUTTON_ADD,
                onClick: handleSubmit(onConfirmCustom),
                disabled: !isCustomSizeReady,
              }}
            />
          </DialogActions>
        </ModalBasicLayout>
      </>
    </Box>
  );
};

export default ArtboardSwitcher;
